import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Patient } from "../../services/patientsService";

export interface Provider {
  id: number;
  name: string;
}

interface DashboardState {
  patients: Patient[];
  providers: Provider[];
  selectedProviderIds: number[];
  // Filters and pagination
  searchTerm: string;
  activeTab: 1 | 2;
  sortBy: string;
  sortOrder: "asc" | "desc";
  currentPage: number;
  pageSize: number;
  totalCount: number;
  // Loading/Error states
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  patients: [],
  providers: [],
  selectedProviderIds: [],
  // Filters and pagination
  searchTerm: "",
  activeTab: 1,
  sortBy: "createdAt",
  sortOrder: "desc",
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  // Loading/Error states
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setPatients: (state, action: PayloadAction<Patient[]>) => {
      state.patients = action.payload;
    },
    setProviders: (state, action: PayloadAction<Provider[]>) => {
      state.providers = action.payload;
    },
    setSelectedProviderIds: (state, action: PayloadAction<number[]>) => {
      state.selectedProviderIds = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    // Filter and pagination actions
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to page 1 when search changes
    },
    setActiveTab: (state, action: PayloadAction<1 | 2>) => {
      state.activeTab = action.payload;
      state.currentPage = 1; // Reset to page 1 when tab changes
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.currentPage = 1; // Reset to page 1 when sort changes
    },
    setSortOrder: (state, action: PayloadAction<"asc" | "desc">) => {
      state.sortOrder = action.payload;
      state.currentPage = 1; // Reset to page 1 when sort changes
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    clearDashboard: (state) => {
      state.patients = [];
      state.providers = [];
      state.selectedProviderIds = [];
      state.searchTerm = "";
      state.activeTab = 1;
      state.sortBy = "createdAt";
      state.sortOrder = "desc";
      state.currentPage = 1;
      state.totalCount = 0;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setPatients,
  setProviders,
  setSelectedProviderIds,
  setLoading,
  setError,
  setSearchTerm,
  setActiveTab,
  setSortBy,
  setSortOrder,
  setCurrentPage,
  setTotalCount,
  clearDashboard,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
